import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";
import { useAuthStore } from "./auth";

/**
 * Workspace-membership read + switch hooks. Powers the popover in
 * `SidebarWorkspaceMenu` (top of the sidebar) when an operator is a
 * member of more than one workspace (Option-C: same email,
 * MEMBER/ADMIN in N tenants but OWNER in only one).
 *
 *   useWorkspaces()       → GET /api/v1/auth/workspaces
 *   useSwitchWorkspace()  → POST /api/v1/auth/workspaces/switch
 *
 * `useSwitchWorkspace.onSuccess` overwrites the access token in the
 * auth store and invalidates every React Query cache — every cached
 * value was scoped to the previous tenant and is now stale.
 */

export function useWorkspaces() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["auth", "workspaces"],
    enabled: !!accessToken,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/auth/workspaces", {})),
  });
}

export function useSwitchWorkspace() {
  const qc = useQueryClient();
  const setTokens = useAuthStore((s) => s.setTokens);
  const currentEmail = useAuthStore((s) => s.email);
  return useMutation({
    mutationFn: async (tenantId: string) =>
      unwrap(
        await api.POST("/api/v1/auth/workspaces/switch", {
          body: { tenantId },
        }),
      ),
    onSuccess: (resp) => {
      // Email stays the same across workspaces (it's the operator's
      // identity); only the tenant scope flips. The MFA branch on
      // LoginResponse can't fire here (caller is already signed in), so
      // accessToken is always present.
      if (!resp.accessToken) return;
      setTokens(resp.accessToken, currentEmail ?? "");
      // Every cached value was tenant-scoped — drop them all and let
      // the dashboard repopulate against the new tenant.
      qc.invalidateQueries();
    },
  });
}

/**
 * Create a new workspace owned by the *currently signed-in* user.
 * Pair to the public `/auth/signup` flow, which is blocked for
 * established email-owners (Option-C). This authenticated path is
 * for the in-dashboard "I want a second workspace" use case.
 *
 * On success the JWT pair is rotated to the new tenant — same
 * contract as `useSwitchWorkspace`, so all React Query caches
 * invalidate and the dashboard repaints against the new workspace.
 */
export function useCreateWorkspace() {
  const qc = useQueryClient();
  const setTokens = useAuthStore((s) => s.setTokens);
  const currentEmail = useAuthStore((s) => s.email);
  return useMutation({
    mutationFn: async (body: {
      workspaceName: string;
      countryIso2?: string;
    }) =>
      unwrap(
        await api.POST("/api/v1/auth/workspaces", {
          body,
        }),
      ),
    onSuccess: (resp) => {
      // MFA challenge can't apply here (already signed in).
      if (!resp.accessToken) return;
      setTokens(resp.accessToken, currentEmail ?? "");
      qc.invalidateQueries();
    },
  });
}
